/**
 * User-facing logins API service
 */

import { api } from '@services/api';
import { API_PATHS } from '@utils/constants';
import {
  LoginStatusListSchema,
  LoginStatusSchema,
  LoginStartSchema,
  type LoginStatus,
  type LoginStart,
} from '../types';

const qs = (params: Record<string, string | undefined>): string => {
  const p = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) p.set(key, value);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const loginsApi = {
  /**
   * List the current user's login statuses across all enabled providers.
   */
  list: async (): Promise<LoginStatus[]> => {
    const response = await api.get(API_PATHS.LOGINS);
    const data: unknown = response.data;
    return LoginStatusListSchema.parse(data);
  },

  /**
   * Begin the direct-OIDC login flow for a provider.
   */
  start: async (name: string, redirectUri?: string): Promise<LoginStart> => {
    const response = await api.post(
      `${API_PATHS.LOGINS}/${name}/start${qs({ redirect_uri: redirectUri })}`,
    );
    const data: unknown = response.data;
    return LoginStartSchema.parse(data);
  },

  /**
   * Complete the direct-OIDC login flow after redirect back from the IdP.
   */
  completeCallback: async (
    provider: string,
    code: string,
    codeVerifier: string,
    redirectUri?: string,
  ): Promise<LoginStatus> => {
    const response = await api.get(
      `${API_PATHS.LOGINS}/callback${qs({
        provider,
        code,
        code_verifier: codeVerifier,
        redirect_uri: redirectUri,
      })}`,
    );
    const data: unknown = response.data;
    return LoginStatusSchema.parse(data);
  },

  /**
   * Store a pasted secret for a static-credential provider.
   */
  setStaticSecret: async (name: string, secret: string): Promise<LoginStatus> => {
    const response = await api.put(`${API_PATHS.LOGINS}/${name}`, { secret });
    const data: unknown = response.data;
    return LoginStatusSchema.parse(data);
  },

  /**
   * Disconnect a login for the current user.
   */
  disconnect: async (name: string): Promise<void> => {
    await api.delete(`${API_PATHS.LOGINS}/${name}`);
  },
};
